/**
 * marketHours.js
 * Helpers compartidos para calcular la hora local de Madrid y el estado
 * combinado de los mercados (Europa + EE.UU.) a partir del campo
 * `market_state` que ya viene enriquecido en cada activo.
 *
 * Nunca uses `new Date().getHours()` directamente: en producción (contenedor
 * Kubernetes) devuelve la hora UTC. Usa siempre `getMadridTime()`.
 */
import { isEuropeanTicker, isMexicanTicker, isCryptoTicker } from '../components/markets/constants';
import { getMarketHolidays } from './marketHolidays';

// -----------------------------------------------------------------------------
// Hora local de Madrid (Europe/Madrid)
// -----------------------------------------------------------------------------
export const getMadridTime = () => {
  const str = new Date().toLocaleString('en-US', {
    timeZone: 'Europe/Madrid',
    hour12: false,
  });
  // Formato: "MM/DD/YYYY, HH:mm:ss"
  const timePart = str.split(', ')[1] || '00:00:00';
  const [hStr, mStr] = timePart.split(':');
  let hour = parseInt(hStr, 10);
  if (hour === 24) hour = 0; // Chrome legacy quirk
  const minutes = parseInt(mStr, 10) || 0;

  // Día de la semana en Madrid (0 = domingo, 6 = sábado)
  const madridDateStr = str.split(', ')[0] || '';
  const [mm, dd, yyyy] = madridDateStr.split('/').map((n) => parseInt(n, 10));
  const dayOfWeek = Number.isFinite(mm)
    ? new Date(Date.UTC(yyyy, mm - 1, dd)).getUTCDay()
    : new Date().getDay();

  return { hour, minutes, dayOfWeek };
};

// -----------------------------------------------------------------------------
// Fecha local de Madrid en formato YYYY-MM-DD (para comparar con festivos).
// -----------------------------------------------------------------------------
export const getMadridDateKey = () => {
  const str = new Date().toLocaleString('en-US', {
    timeZone: 'Europe/Madrid',
    hour12: false,
  });
  const datePart = str.split(', ')[0] || '';
  const [mm, dd, yyyy] = datePart.split('/').map((n) => parseInt(n, 10));
  if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${yyyy}-${p(mm)}-${p(dd)}`;
};

// -----------------------------------------------------------------------------
// Hora local de México (America/Mexico_City). Mismo patrón que getMadridTime:
// nunca uses new Date().getHours() (el contenedor devuelve UTC).
// -----------------------------------------------------------------------------
export const getMexicoTime = () => {
  const str = new Date().toLocaleString('en-US', {
    timeZone: 'America/Mexico_City',
    hour12: false,
  });
  const timePart = str.split(', ')[1] || '00:00:00';
  const [hStr, mStr] = timePart.split(':');
  let hour = parseInt(hStr, 10);
  if (hour === 24) hour = 0;
  const minutes = parseInt(mStr, 10) || 0;
  const dateStr = str.split(', ')[0] || '';
  const [mm, dd, yyyy] = dateStr.split('/').map((n) => parseInt(n, 10));
  const dayOfWeek = Number.isFinite(mm)
    ? new Date(Date.UTC(yyyy, mm - 1, dd)).getUTCDay()
    : new Date().getDay();
  return { hour, minutes, dayOfWeek };
};

// Fecha local de México en formato YYYY-MM-DD (para comparar con festivos BMV).
export const getMexicoDateKey = () => {
  const str = new Date().toLocaleString('en-US', {
    timeZone: 'America/Mexico_City',
    hour12: false,
  });
  const datePart = str.split(', ')[0] || '';
  const [mm, dd, yyyy] = datePart.split('/').map((n) => parseInt(n, 10));
  if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${yyyy}-${p(mm)}-${p(dd)}`;
};

// -----------------------------------------------------------------------------
// Hora local de Nueva York (America/New_York). Mismo patrón: nunca uses
// new Date().getHours() (el contenedor devuelve UTC). Así el estado de EE.UU.
// es correcto TODO el año, incluidas las semanas en que el cambio de horario
// de verano de EE.UU. y Europa no coinciden (marzo/octubre-noviembre).
// -----------------------------------------------------------------------------
export const getNewYorkTime = () => {
  const str = new Date().toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
  });
  const timePart = str.split(', ')[1] || '00:00:00';
  const [hStr, mStr] = timePart.split(':');
  let hour = parseInt(hStr, 10);
  if (hour === 24) hour = 0;
  const minutes = parseInt(mStr, 10) || 0;
  const dateStr = str.split(', ')[0] || '';
  const [mm, dd, yyyy] = dateStr.split('/').map((n) => parseInt(n, 10));
  const dayOfWeek = Number.isFinite(mm)
    ? new Date(Date.UTC(yyyy, mm - 1, dd)).getUTCDay()
    : new Date().getDay();
  return { hour, minutes, dayOfWeek };
};

// Fecha local de Nueva York en formato YYYY-MM-DD (para comparar con festivos NYSE).
export const getNewYorkDateKey = () => {
  const str = new Date().toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
  });
  const datePart = str.split(', ')[0] || '';
  const [mm, dd, yyyy] = datePart.split('/').map((n) => parseInt(n, 10));
  if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${yyyy}-${p(mm)}-${p(dd)}`;
};

// -----------------------------------------------------------------------------
// Festivos bursátiles (días SIN sesión). Formato YYYY-MM-DD.
// Europa y EE.UU. tienen calendarios distintos: un mercado puede estar cerrado
// mientras el otro opera. Estos calendarios son la fuente PRIMARIA fiable; como
// red de seguridad, la tarjeta también respeta market_state === 'CLOSED' de
// Yahoo (ver SortableMarketCard). Ampliar cada año (o el override de Yahoo lo
// cubrirá igualmente).
// -----------------------------------------------------------------------------
// NYSE / NASDAQ (mercado EE.UU.) → fecha: nombre en español
export const US_MARKET_HOLIDAYS = new Map([
  // 2026
  ['2026-01-01', 'Año Nuevo'],
  ['2026-01-19', 'Día de Martin Luther King'],
  ['2026-02-16', 'Día de los Presidentes'],
  ['2026-04-03', 'Viernes Santo'],
  ['2026-05-25', 'Día de los Caídos'],
  ['2026-06-19', 'Juneteenth'],
  ['2026-07-03', 'Día de la Independencia'],
  ['2026-09-07', 'Día del Trabajo (EE.UU.)'],
  ['2026-11-26', 'Acción de Gracias'],
  ['2026-12-25', 'Navidad'],
  // 2027
  ['2027-01-01', 'Año Nuevo'],
  ['2027-01-18', 'Día de Martin Luther King'],
  ['2027-02-15', 'Día de los Presidentes'],
  ['2027-03-26', 'Viernes Santo'],
  ['2027-05-31', 'Día de los Caídos'],
  ['2027-06-18', 'Juneteenth'],
  ['2027-07-05', 'Día de la Independencia'],
  ['2027-09-06', 'Día del Trabajo (EE.UU.)'],
  ['2027-11-25', 'Acción de Gracias'],
  ['2027-12-24', 'Navidad'],
]);

// BME (Madrid) / Xetra (Frankfurt) / Euronext — festivos comunes europeos.
export const EU_MARKET_HOLIDAYS = new Map([
  // 2026
  ['2026-01-01', 'Año Nuevo'],
  ['2026-04-03', 'Viernes Santo'],
  ['2026-04-06', 'Lunes de Pascua'],
  ['2026-05-01', 'Día del Trabajo'],
  ['2026-12-24', 'Nochebuena'],
  ['2026-12-25', 'Navidad'],
  ['2026-12-31', 'Nochevieja'],
  // 2027
  ['2027-01-01', 'Año Nuevo'],
  ['2027-03-26', 'Viernes Santo'],
  ['2027-03-29', 'Lunes de Pascua'],
  ['2027-12-24', 'Nochebuena'],
  ['2027-12-31', 'Nochevieja'],
]);

// Devuelven el NOMBRE del festivo (o null si hoy no es festivo de ese mercado).
// Ahora se calculan PROGRAMÁTICAMENTE para cualquier año (marketHolidays.js);
// los Map estáticos de arriba quedan como referencia/histórico.
const _madridYear = () => parseInt(getMadridDateKey().slice(0, 4), 10) || new Date().getUTCFullYear();
const _mexicoYear = () => parseInt(getMexicoDateKey().slice(0, 4), 10) || new Date().getUTCFullYear();
const _usYear = () => parseInt(getNewYorkDateKey().slice(0, 4), 10) || new Date().getUTCFullYear();

export const getUSMarketHolidayToday = () => getMarketHolidays('US', _usYear()).get(getNewYorkDateKey()) || null;
export const getEUMarketHolidayToday = () => getMarketHolidays('EU', _madridYear()).get(getMadridDateKey()) || null;
export const getMXMarketHolidayToday = () => getMarketHolidays('MX', _mexicoYear()).get(getMexicoDateKey()) || null;

export const isUSMarketHolidayToday = () => !!getUSMarketHolidayToday();
export const isEUMarketHolidayToday = () => !!getEUMarketHolidayToday();
export const isMXMarketHolidayToday = () => !!getMXMarketHolidayToday();

// -----------------------------------------------------------------------------
// Fallback basado únicamente en horas de Madrid (cuando no hay market_state)
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// Fase por franjas horarias de Madrid (fuente PRIMARIA, se usa siempre).
// Europa 09:00-17:30 · EE.UU. pre 10:00-15:30, regular 15:30-22:00,
// post 22:00-~02:00. Fuera de rango o fin de semana => 'closed'.
// -----------------------------------------------------------------------------
const europeStatusByHours = () => {
  const { hour, minutes, dayOfWeek } = getMadridTime();
  if (dayOfWeek === 0 || dayOfWeek === 6) return 'closed';
  const time = hour * 60 + minutes;
  // Pre-mercado europeo 08:00 – 09:00 (subasta de apertura Xetra / Tradegate temprano)
  if (time >= 7 * 60 + 30 && time < 9 * 60) return 'pre';
  // Sesión regular 09:00 – 17:30
  if (time >= 9 * 60 && time < 17 * 60 + 30) return 'open';
  // Extendido (Tradegate/LS afterhours) 17:30 – 23:00 (ventana Trade Republic)
  if (time >= 17 * 60 + 30 && time < 23 * 60) return 'post';
  return 'closed';
};

const usStatusByHours = () => {
  // EE.UU. se calcula SIEMPRE con su propia hora local (America/New_York),
  // igual que Europa (Madrid) y México. Así el estado es correcto todo el año,
  // sin desfases en las semanas de cambio de horario de verano.
  const { hour, minutes, dayOfWeek } = getNewYorkTime();
  if (dayOfWeek === 0 || dayOfWeek === 6) return 'closed';
  const time = hour * 60 + minutes;
  // Pre-mercado 04:00 – 09:30 (hora de Nueva York)
  if (time >= 4 * 60 && time < 9 * 60 + 30) return 'pre';
  // Sesión regular 09:30 – 16:00
  if (time >= 9 * 60 + 30 && time < 16 * 60) return 'open';
  // Post-mercado 16:00 – 20:00
  if (time >= 16 * 60 && time < 20 * 60) return 'post';
  return 'closed';
};

const mexicoStatusByHours = () => {
  const { hour, minutes, dayOfWeek } = getMexicoTime();
  if (dayOfWeek === 0 || dayOfWeek === 6) return 'closed';
  const time = hour * 60 + minutes;
  // Pre-apertura (subasta) 08:00 – 08:30
  if (time >= 8 * 60 && time < 8 * 60 + 30) return 'pre';
  // Sesión regular 08:30 – 15:00
  if (time >= 8 * 60 + 30 && time < 15 * 60) return 'open';
  // Post-cierre (subasta de cierre) 15:00 – 15:10
  if (time >= 15 * 60 && time < 15 * 60 + 10) return 'post';
  return 'closed';
};

// -----------------------------------------------------------------------------
// Etiqueta combinada Europa + EE.UU. + México.
// -----------------------------------------------------------------------------
const seg = (name, s) => {
  if (s === 'open') return `${name} abierto`;
  if (s === 'pre') return `${name} en pre-mercado`;
  if (s === 'post') return `${name} en post-mercado`;
  return `${name} cerrado`;
};

const buildLabel = (europe, us, mexico) => {
  if (europe === 'open' && us === 'open' && mexico === 'open') return 'Mercados abiertos';
  if (europe === 'closed' && us === 'closed' && mexico === 'closed') return 'Mercados cerrados';
  return `${seg('Europa', europe)} · ${seg('EE.UU.', us)} · ${seg('México', mexico)}`;
};

// -----------------------------------------------------------------------------
// getMarketStatus(enrichedMarkets)
//
// Reglas:
//   1) La FASE (open/pre/post/closed) se calcula SIEMPRE por franjas horarias
//      de Madrid, tanto durante como después de la carga. Nunca se deriva de
//      market_state === 'REGULAR' de un activo suelto.
//   2) market_state SOLO se usa para forzar 'closed' (festivos): si TODOS los
//      activos relevantes de un mercado están en CLOSED, se fuerza cerrado
//      aunque la franja horaria diga abierto. Nunca se usa para forzar abierto.
//
// Devuelve { europe, us, label, anyOpen }.
// -----------------------------------------------------------------------------
export const getMarketStatus = (enrichedMarkets = []) => {
  // Fase principal: siempre por hora local de cada mercado.
  let europe = europeStatusByHours();
  let us = usStatusByHours();
  let mexico = mexicoStatusByHours();

  // Override directo de festivos (calendario programático).
  if (isEUMarketHolidayToday()) europe = 'closed';
  if (isUSMarketHolidayToday()) us = 'closed';
  if (isMXMarketHolidayToday()) mexico = 'closed';

  // Recopilar market_state por mercado para el override de festivos.
  let euCount = 0, euClosedCount = 0;
  let usCount = 0, usClosedCount = 0;
  let mxCount = 0, mxClosedCount = 0;

  for (const m of enrichedMarkets) {
    if (!m || !m.ticker) continue;
    if (isCryptoTicker(m.ticker)) continue;

    const raw = m.market_state || m.marketState;
    if (!raw) continue;
    const s = String(raw).toUpperCase();

    if (isMexicanTicker(m.ticker)) {
      mxCount += 1;
      if (s === 'CLOSED') mxClosedCount += 1;
    } else if (isEuropeanTicker(m.ticker)) {
      euCount += 1;
      if (s === 'CLOSED') euClosedCount += 1;
    } else {
      usCount += 1;
      if (s === 'CLOSED') usClosedCount += 1;
    }
  }

  // Override de festivos: si TODOS los activos del mercado están CLOSED,
  // forzamos ese mercado a 'closed'. Nunca abrimos con market_state.
  if (euCount > 0 && euClosedCount === euCount) europe = 'closed';
  if (usCount > 0 && usClosedCount === usCount) us = 'closed';
  if (mxCount > 0 && mxClosedCount === mxCount) mexico = 'closed';

  return {
    europe,
    us,
    mexico,
    label: buildLabel(europe, us, mexico),
    anyOpen: europe === 'open' || us === 'open' || mexico === 'open',
  };
};
